import axios from 'axios';
import clsx from 'clsx';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { createPath, useLocation } from 'react-router-dom';

import type { PlansResponse } from '@flintmint/core-shared/billing';
import type { UserAccountDoc } from '@flintmint/core-shared/firestore/schemas';
import { formatCount, formatTrialAllowanceRow, formatTypicalNotes } from '@flintmint/core-shared/utils';
import { Button } from '@flintmint/web-shared/components/button';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Skeleton } from '@flintmint/web-shared/components/skeleton';
import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';

import { urlOverlaySearch, useUrlOverlay } from '@/hooks/use-url-overlay';
import { usePlans } from '@/hooks/use-plans';
import { useUserAccount } from '@/hooks/use-user-account';
import { useAppSettings } from '@/hooks/use-app-settings';
import { appRoutes } from '@/constants/routes';
import { urlOverlays } from '@/constants/url-overlays';
import { FullScreenOverlay, FullScreenOverlayPresence, OverlayNote, OverlayPanel } from '@/components/full-screen-overlay';

import {
  canCancelPendingSubscription,
  canChoosePlan,
  canOpenPortal,
  isFirstChargeRetrying,
  isTrialEligible,
} from '../billing-state';
import {
  addCalendarMonths,
  DAY_MS,
  formatBillingDate,
  formatBillingDayMonth,
  formatMonthlyEnergyRow,
  formatPrice,
  formatPriceWithCents,
  intervalLabels,
} from '../billing-format';
import { type SkeletonLineStyle, Text, TextSkeleton, type TextStyle } from '../billing-text';
import { useOpenPortal } from '../use-open-portal';
import { useReturnFocus } from '../use-return-focus';
import { useStripeRedirect } from '../use-stripe-redirect';

import styles from './plan-picker.module.scss';

/** Mounted once over every screen; opened by `useUrlOverlay(urlOverlays.planPicker, urlOverlays)`. */
export const PlanPicker = () => {
  const { isOpen, close } = useUrlOverlay(urlOverlays.planPicker, urlOverlays);

  return (
    <FullScreenOverlayPresence open={isOpen}>
      <PlanPickerGate onClose={close} />
    </FullScreenOverlayPresence>
  );
};

/** Checkout answers 409 unless the account can choose a plan, so any other account closes the picker unseen. */
const PlanPickerGate = ({ onClose }: { onClose: () => void }) => {
  const { account, loading } = useUserAccount();
  const denied = !loading && !canChoosePlan(account);

  useEffect(() => {
    if (denied) onClose();
  }, [denied, onClose]);

  return !denied && <PlanPickerDialog account={account} accountLoading={loading} onClose={onClose} />;
};

type PlanPickerDialogProps = { account: UserAccountDoc | undefined; accountLoading: boolean; onClose: () => void };

/**
 * The picker for an account with the Trial available or already used. Loading renders one placeholder card per cached
 * plan (else three). While the account loads, its Trial eligibility is unknown, so the Trial layout renders with every
 * text and button replaced in place by a skeleton of its size, and the most common state loads with no layout shift.
 * An account that already used its Trial shows Compare plans with all its static copy, and only the cards load.
 */
const PlanPickerDialog = ({ account, accountLoading, onClose }: PlanPickerDialogProps) => {
  const { data, isLoading: plansLoading, isError } = usePlans(true);
  const { startCheckout, choosing } = useStartCheckout(onClose);
  const { openPortal, opening } = useOpenPortal();
  const { switchTo } = useUrlOverlay(urlOverlays.planPicker, urlOverlays);
  const creditsPerTypicalNote = useAppSettings()?.creditsPerTypicalNote;
  // Leaving would strand a Checkout that is about to open.
  const closeUnlessChoosing = () => {
    if (choosing === undefined) onClose();
  };

  const loading = accountLoading || plansLoading;
  const trialEligible = isTrialEligible(account);
  const comparing = !accountLoading && !trialEligible;
  // The Trial layout's placeholders stand in for its text too; Compare plans' copy is static, so it shows while loading.
  const trialLoading = loading && !comparing;
  const copy = !comparing ? getTrialCopy(data) : account?.trialUsedAt === undefined ? returningCopy : afterCompCopy;
  const plans = loading ? undefined : data?.plans;
  // The recommended plan, else the cheapest, starts selected, and is selected again if a refetch drops the chosen one.
  const [selectedId, setSelectedId] = useState<string>();
  const recommendedId = data?.recommendedStripeProductId;
  const isPlan = (stripeProductId: string | undefined) => plans?.some((plan) => plan.stripeProductId === stripeProductId);
  const selected = isPlan(selectedId) ? selectedId : isPlan(recommendedId) ? recommendedId : plans?.[0]?.stripeProductId;

  // Trial-eligible and returning accounts: the chosen plan is reviewed before Checkout, in this same
  // overlay. While Stripe retries a first charge, Checkout answers 409, so the step says so instead and offers
  // paying or cancelling it. The step is not in the URL, so a reload or a cancelled Checkout returns to the plans.
  // It holds the reviewed plan, which a refetch never swaps, when the review started, for the dates, and which
  // review it is, which a subscription stored while it is open never swaps.
  const [review, setReview] = useState<{ stripeProductId: string; startedAt: number; kind: ReviewKind }>();
  const reviewedPlan = review && plans?.find((plan) => plan.stripeProductId === review.stripeProductId);
  const reviewing =
    review && reviewedPlan && data
      ? { kind: review.kind, plan: reviewedPlan, trial: data, startedAt: review.startedAt, creditsPerTypicalNote }
      : undefined;
  const reviewCopy = reviewing && reviewCopies[reviewing.kind];
  const backToPlans = () => {
    if (choosing === undefined && !opening) setReview(undefined);
  };
  const selectedActionRef = useRef<HTMLButtonElement>(null);
  useReturnFocus(review !== undefined, selectedActionRef);

  // A refetch that drops the reviewed plan ends the review instead of showing another plan.
  useEffect(() => {
    if (review && !loading && !reviewedPlan) setReview(undefined);
  }, [review, loading, reviewedPlan]);

  return (
    <FullScreenOverlay
      title={
        reviewCopy ? (
          reviewCopy.title
        ) : (
          <Text style={textStyles.title} loading={trialLoading}>
            {copy.title}
          </Text>
        )
      }
      subtitle={
        reviewCopy ? (
          reviewCopy.subtitle
        ) : trialLoading ? (
          <TextSkeleton style={textStyles.body} width={skeletonWidths.trialSubtitle} />
        ) : (
          copy.subtitle
        )
      }
      loading={loading}
      loadingLabel="Plans"
      size={reviewing ? 'notice' : 'wide'}
      view={reviewing ? 'review' : 'plans'}
      onEscape={reviewing ? backToPlans : closeUnlessChoosing}
      onClose={reviewing ? undefined : closeUnlessChoosing}
      closeDisabled={choosing !== undefined}
    >
      {reviewing && reviewCopy ? (
        <>
          {reviewing.kind === 'trial' ? (
            <TrialReview {...reviewing} />
          ) : reviewing.kind === 'returning' ? (
            <ReturningReview
              plan={reviewing.plan}
              startedAt={reviewing.startedAt}
              creditsPerTypicalNote={reviewing.creditsPerTypicalNote}
            />
          ) : (
            <FirstChargeRetryingNotice />
          )}
          <div className={styles.reviewActions}>
            {reviewing.kind === 'firstChargeRetrying' ? (
              <>
                {canOpenPortal(account) && (
                  <Button size="big" fullWidth cornerRadius="smooth" color="hero" loading={opening} onClick={openPortal}>
                    Update payment method
                  </Button>
                )}
                {canCancelPendingSubscription(account) && (
                  <Button
                    size="big"
                    fullWidth
                    cornerRadius="smooth"
                    appearance="clear"
                    disabled={opening}
                    onClick={() => switchTo(urlOverlays.cancelPendingSubscription)}
                  >
                    Cancel pending subscription
                  </Button>
                )}
              </>
            ) : (
              <Button
                size="big"
                fullWidth
                cornerRadius="smooth"
                loading={choosing === reviewing.plan.stripeProductId}
                onClick={() => startCheckout(reviewing.plan.stripeProductId)}
              >
                {reviewCopies[reviewing.kind].start}
              </Button>
            )}
            <Button
              size="big"
              fullWidth
              cornerRadius="smooth"
              appearance="clear"
              disabled={choosing !== undefined || opening}
              onClick={backToPlans}
            >
              Back to plans
            </Button>
          </div>
        </>
      ) : (
        <>
          {isError && !loading ? (
            <p className={styles.failure}>Plans can’t be loaded right now. Try again later.</p>
          ) : (
            <div className={styles.plans} role={loading ? undefined : 'radiogroup'} aria-label={loading ? undefined : 'Plans'}>
              {loading &&
                Array.from({ length: data?.plans.length ?? PLACEHOLDER_PLAN_COUNT }, (_, index) => (
                  <PlanCardSkeleton
                    key={index}
                    bars={comparing ? planCardSkeletonBars.compare : planCardSkeletonBars.trial}
                    estimate={creditsPerTypicalNote !== undefined}
                  />
                ))}
              {plans?.map((plan) => (
                <PlanCard
                  key={plan.stripeProductId}
                  plan={plan}
                  creditsPerTypicalNote={creditsPerTypicalNote}
                  recommended={plan.stripeProductId === recommendedId}
                  selected={plan.stripeProductId === selected}
                  selectedAction={copy.selectedAction(plan.planName)}
                  selectedActionRef={selectedActionRef}
                  choosing={choosing}
                  onSelect={() => setSelectedId(plan.stripeProductId)}
                  onChoose={() =>
                    setReview({
                      stripeProductId: plan.stripeProductId,
                      startedAt: Date.now(),
                      kind: trialEligible
                        ? 'trial'
                        : isFirstChargeRetrying(account?.stripeSubscription)
                        ? 'firstChargeRetrying'
                        : 'returning',
                    })
                  }
                />
              ))}
            </div>
          )}

          {/* Static, so it shows even while the app settings load. */}
          <div className={styles.footnote}>
            <Text style={textStyles.footnote} loading={trialLoading}>
              Typical-note estimates vary with length and complexity.
            </Text>
          </div>

          <div className={styles.strip}>
            <div className={styles.stripText}>
              <div>
                <Text style={textStyles.strip} loading={trialLoading}>
                  {copy.stripTitle}
                </Text>
              </div>
              <div>
                <Text style={textStyles.strip} loading={trialLoading}>
                  {copy.stripMessage}
                </Text>
              </div>
            </div>
            {trialLoading ? (
              <Skeleton height={46} trueWidth={200} borderRadius={12} />
            ) : (
              copy.stripAction && (
                <Button
                  size="big"
                  cornerRadius="smooth"
                  color="grey"
                  className={styles.stripAction}
                  disabled={choosing !== undefined}
                  onClick={closeUnlessChoosing}
                >
                  {copy.stripAction}
                </Button>
              )
            )}
          </div>
        </>
      )}
    </FullScreenOverlay>
  );
};

/** `onClose` closes the picker in place when Checkout answers that a subscription already exists. */
const useStartCheckout = (onClose: () => void) => {
  const { showToastNotification } = useToaster();
  const location = useLocation();
  const { redirect, pending: choosing } = useStripeRedirect({
    errorTitle: 'Checkout could not be opened. Try again.',
    onError: (error) => {
      if (!(axios.isAxiosError(error) && error.response?.status === 409)) return false;
      showToastNotification({ type: 'info', title: 'You already have a subscription' });
      onClose();
      return true;
    },
  });

  const startCheckout = (stripeProductId: string) =>
    redirect(stripeProductId, (api) =>
      api.createCheckoutSession({
        stripeProductId,
        interval: 'month',
        successUrl: `${window.location.origin}${appRoutes.settingsBilling}${urlOverlaySearch(urlOverlays.checkoutSuccess)}`,
        // The current screen with the picker open, which carries its own param.
        cancelUrl: `${window.location.origin}${createPath(location)}`,
      })
    );

  return { startCheckout, choosing };
};

type Plan = PlansResponse['plans'][number];

/**
 * A plan card's layout with a bar in the line box of each row, so the card keeps its height when the plans load. None
 * is marked selected, so no highlight jumps when the plans load.
 */
const PlanCardSkeleton = ({ bars, estimate }: { bars: PlanCardSkeletonBars; estimate: boolean }) => (
  <section className={styles.planCard} data-testid="plan-card-skeleton">
    <Skeleton trueHeight={textStyles.planName.lineHeight} {...bars.planName} />
    <Skeleton trueHeight={textStyles.price.lineHeight} {...bars.price} />
    <Skeleton {...bars.action} />
    <div>
      <div className={styles.allowance}>
        <Skeleton trueHeight={textStyles.allowance.lineHeight} {...bars.monthlyJoules} />
      </div>
      {estimate && (
        <div className={styles.allowance}>
          <Skeleton trueHeight={textStyles.allowance.lineHeight} {...bars.typicalNotes} />
        </div>
      )}
    </div>
  </section>
);

type PlanCardProps = {
  plan: Plan;
  creditsPerTypicalNote: number | undefined;
  /** Marked Recommended, which is part of the card's and its radio's name. */
  recommended: boolean;
  selected: boolean;
  selectedAction: string;
  selectedActionRef: RefObject<HTMLButtonElement | null>;
  choosing: string | undefined;
  onSelect: () => void;
  onChoose: () => void;
};

/**
 * A single-choice card: a click anywhere on it, its Choose button, or its radio (Tab into the group, then the
 * arrow keys) selects it, and only the selected card's button opens Checkout. Nothing changes while one opens.
 */
const PlanCard = (props: PlanCardProps) => {
  const { plan, creditsPerTypicalNote, recommended, selected, selectedAction, selectedActionRef, choosing, onSelect, onChoose } =
    props;
  const { stripeProductId, planName, monthlyAmountCents, monthlyCredits } = plan;
  const typicalNotes = formatTypicalNotes(monthlyCredits, creditsPerTypicalNote);
  const titleId = `plan-${stripeProductId}`;
  const badgeId = `${titleId}-badge`;
  const labelledBy = recommended ? `${titleId} ${badgeId}` : titleId;
  const select = () => {
    if (choosing === undefined) onSelect();
  };

  return (
    <section className={clsx(styles.planCard, { [styles.selected]: selected })} aria-labelledby={labelledBy} onClick={select}>
      <input
        type="radio"
        name="plan-picker-plan"
        className={styles.radio}
        checked={selected}
        disabled={choosing !== undefined}
        aria-labelledby={labelledBy}
        onChange={select}
      />
      <div className={styles.titleRow}>
        <h2 id={titleId}>{planName}</h2>
        {recommended && (
          <span id={badgeId} className={styles.badge}>
            Recommended
          </span>
        )}
      </div>
      <div className={styles.price}>{`${formatPrice(monthlyAmountCents)} / month`}</div>
      <Button
        ref={selected ? selectedActionRef : undefined}
        size="big"
        cornerRadius="smooth"
        fullWidth
        loading={choosing === stripeProductId}
        disabled={choosing !== undefined && choosing !== stripeProductId}
        onClick={selected ? onChoose : select}
      >
        {selected ? selectedAction : `Choose ${planName}`}
      </Button>
      <div>
        <div className={styles.allowance}>
          <Icon type="tick-02" size={14} aria-hidden="true" />
          {`${formatCount(monthlyCredits)} Monthly Joules`}
        </div>
        {typicalNotes && (
          <div className={styles.allowance}>
            {/* The icon set has no "approximately" icon, so the glyph takes the tick's slot. */}
            <span className={styles.approximately} aria-hidden="true" />
            {typicalNotes}
          </div>
        )}
      </div>
    </section>
  );
};

type ReviewProps = {
  plan: Plan;
  trial: Pick<PlansResponse, 'trialDays' | 'trialCredits'>;
  startedAt: number;
  creditsPerTypicalNote: number | undefined;
};

type ReviewKind = 'trial' | 'returning' | 'firstChargeRetrying';

const reviewCopies = {
  trial: { title: 'Start your Trial', subtitle: 'Finish setting up your Trial securely in Stripe.', start: 'Start free Trial' },
  returning: { title: 'Review your subscription', subtitle: 'Review and pay securely in Stripe.', start: 'Continue in Stripe' },
  firstChargeRetrying: {
    title: 'Your first payment didn’t go through',
    subtitle: 'Your paid plan is pending. Your selected price is saved.',
  },
} satisfies Record<ReviewKind, { title: string; subtitle: string; start?: string }>;

/**
 * The review body for a Trial-eligible account, without its promo row and offer sentence. The first charge is the client's estimate (the
 * backend grants a Trial of `trialDays`; Stripe sets the exact date). Every value is in hand, so nothing loads.
 */
const TrialReview = ({ plan, trial, startedAt, creditsPerTypicalNote }: ReviewProps) => {
  const firstChargeAt = new Date(startedAt + trial.trialDays * DAY_MS).toISOString();
  const firstCharge = formatBillingDate(firstChargeAt);
  // The footnote's sentence leaves out the year; the design does the same.
  const firstChargeDay = formatBillingDayMonth(firstChargeAt);

  return (
    <>
      <OverlayPanel title={`${trial.trialDays} days free. AI that fits your workflow.`}>
        <div>{`Selected plan: ${plan.planName} · ${intervalLabels.month}`}</div>
        <div>{formatTrialAllowanceRow(trial, creditsPerTypicalNote)}</div>
        <div>{`First charge: ${firstCharge}`}</div>
      </OverlayPanel>
      <OverlayNote>
        {/* TODO: when plans carry an introductory offer, say "Cancelling stops the scheduled charge and ends your introductory offer." as the design does. */}
        {`Your selected plan starts automatically on ${firstChargeDay} unless you cancel. Cancelling stops the scheduled charge. Any remaining Trial access stays available until ${firstChargeDay}.`}
      </OverlayNote>
    </>
  );
};

/**
 * The review body for an account that already had its Trial, so Checkout charges at once. The next payment
 * is the client's estimate, a calendar month after the review started; Stripe sets the exact date.
 */
const ReturningReview = ({
  plan,
  startedAt,
  creditsPerTypicalNote,
}: Pick<ReviewProps, 'plan' | 'startedAt' | 'creditsPerTypicalNote'>) => {
  const name = `${plan.planName} · ${intervalLabels.month}`;
  const price = formatPriceWithCents(plan.monthlyAmountCents);
  const nextPayment = formatBillingDate(addCalendarMonths(new Date(startedAt), 1).toISOString());

  return (
    <>
      <OverlayPanel title={name}>
        <div>{`Selected plan: ${name}`}</div>
        <div>{`Due today: ${price} USD`}</div>
        <div>{`Next payment: ${price} on ${nextPayment}`}</div>
        <div>{formatMonthlyEnergyRow(plan.monthlyCredits, creditsPerTypicalNote)}</div>
        {/* TODO: show the "Payment" row (card brand and last four) once the backend exposes the default payment method. */}
      </OverlayPanel>
      <OverlayNote>Stripe shows the final total and billing terms before you pay.</OverlayNote>
    </>
  );
};

/** The step's body while Stripe retries a failed first charge (`isFirstChargeRetrying`). */
const FirstChargeRetryingNotice = () => (
  <>
    <OverlayPanel title="Your subscription is still pending">
      <div className={styles.paragraphs}>
        <div>Stripe will retry your payment.</div>
        <div>If your Energy is used up, keep working with Free tools.</div>
      </div>
    </OverlayPanel>
    <OverlayNote>Continuing on Free does not cancel the pending subscription or stop payment retries.</OverlayNote>
  </>
);

type PickerCopy = {
  title: string;
  subtitle: string;
  /** The selected card's button, which opens the review step; the other cards' buttons read "Choose <plan>". */
  selectedAction: (planName: string) => string;
  stripTitle: string;
  stripMessage: string;
  /** A button in the strip that closes the picker; the Trial strip has none. */
  stripAction?: string;
};

/** Without the plans response the Trial's length and Joules are unknown, so the subtitle leaves them out. */
const getTrialCopy = (trial: Pick<PlansResponse, 'trialDays' | 'trialCredits'> | undefined): PickerCopy => {
  const choose = 'Choose your future paid plan and add a payment method. Cancel before Trial ends to avoid a charge.';

  return {
    title: 'Choose what comes after your Trial',
    subtitle: trial
      ? `Start a ${trial.trialDays}-day Trial with ${formatCount(trial.trialCredits)} one-time Joules for AI. ${choose}`
      : choose,
    selectedAction: () => 'Start free Trial',
    stripTitle: 'Not ready for Trial?',
    stripMessage: 'Keep working with manual notes, Blocks and existing content.',
  };
};

/** For an account whose Trial eligibility has already been consumed. */
const returningCopy: PickerCopy = {
  title: 'Compare plans',
  subtitle: 'You have already used your Trial. Choose a paid package and review your payment before continuing.',
  // No card is selected here, so the button label is ours.
  selectedAction: (planName) => `Continue with ${planName}`,
  stripTitle: 'Not ready to restart?',
  stripMessage: 'Keep using your non-AI features.',
  stripAction: 'Keep my current access',
};

/** For a Free account whose comp ended: only a comp stamps `trialUsedAt`, and ending one keeps the stamp. */
const afterCompCopy: PickerCopy = {
  ...returningCopy,
  subtitle: 'Choose a paid plan. Your AI Preview and saved Joules stay with your account.',
};

/** Three cards, shown while loading when no plans response is cached to count. */
const PLACEHOLDER_PLAN_COUNT = 3;

const textStyles = {
  title: { lineHeight: 42, textHeight: 20, charWidth: 13.4 },
  body: { lineHeight: 21, textHeight: 10 },
  planName: { lineHeight: 28, textHeight: 14 },
  price: { lineHeight: 44, textHeight: 24 },
  allowance: { lineHeight: 32, textHeight: 10 },
  footnote: { lineHeight: 18, textHeight: 8, charWidth: 5.5 },
  strip: { lineHeight: 24, textHeight: 13, charWidth: 8.7 },
} satisfies Record<string, TextStyle | SkeletonLineStyle>;

// Loading width of the Trial subtitle, measured in Inter at its text style.
const skeletonWidths = {
  trialSubtitle: 1027,
};

type PlanCardSkeletonBars = Record<
  'planName' | 'price' | 'action' | 'monthlyJoules' | 'typicalNotes',
  { trueWidth: number | string; height: number; borderRadius: number }
>;

/**
 * Each placeholder card row's bar. The Trial layout's are its text's glyph heights at the measured widths of its
 * data-dependent lines, in Inter: plan names 52 to 96, so 90; prices 187 to 213, so 200; the Joules line 161 with its
 * check, so 160; "About 125 typical notes" 177 with its glyph, so 175. Compare plans' are the design's bars; its
 * billing-detail bar is left out, as the loaded card has no such row.
 */
const planCardSkeletonBars = {
  trial: {
    planName: { trueWidth: 90, height: textStyles.planName.textHeight, borderRadius: 4 },
    price: { trueWidth: 200, height: textStyles.price.textHeight, borderRadius: 4 },
    action: { trueWidth: '100%', height: 46, borderRadius: 12 },
    monthlyJoules: { trueWidth: 160, height: textStyles.allowance.textHeight, borderRadius: 4 },
    typicalNotes: { trueWidth: 175, height: textStyles.allowance.textHeight, borderRadius: 4 },
  },
  compare: {
    planName: { trueWidth: 100, height: 20, borderRadius: 8 },
    price: { trueWidth: 200, height: 40, borderRadius: 8 },
    action: { trueWidth: 280, height: 46, borderRadius: 8 },
    monthlyJoules: { trueWidth: 220, height: 14, borderRadius: 8 },
    typicalNotes: { trueWidth: 240, height: 14, borderRadius: 8 },
  },
} satisfies Record<string, PlanCardSkeletonBars>;
